import { useState, useEffect, useCallback, useRef } from "react";

const BASE_URL = "https://api.exchangerate-api.com/v4/latest/USD";
// Alternative location API (CORS friendly examples):
// const LOCATION_API = `https://ipinfo.io/json?token=${process.env.REACT_APP_IPINFO_TOKEN}`;
// or use DB-IP: 'https://api.db-ip.com/v2/free/self'
const LOCATION_API = "https://api.db-ip.com/v2/free/self";

const useCurrencyConverter = () => {
  const [exchangeRates, setExchangeRates] = useState(null); // null until loaded
  const [currency, setCurrency] = useState(() => localStorage.getItem("currency") || "USD" );

  const [country, setCountry] = useState(localStorage.getItem("country") || "United States");
  const [flag, setFlag] = useState(localStorage.getItem("flag") || "https://flagcdn.com/us.svg");
  const [loading, setLoading] = useState(true);
  const hasDetectedLocation = useRef(false);


  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(BASE_URL);
        if (!response.ok) throw new Error("Failed to fetch exchange rates");
        const data = await response.json();
        setExchangeRates(data.rates || {});
      } catch (err) {
        console.error("❌ Error fetching rates:", err);
        setExchangeRates({}); // safe fallback
      }
    };

    const detectUserLocation = async () => {
      try {
        const res = await fetch(LOCATION_API);
        if (!res.ok) throw new Error("Failed to fetch location");
        const data = await res.json();

        // Normalize keys depending on API used
        const detectedCurrency = data.currency || data.countryCurrency || "USD";
        const detectedCountry = data.country_name || data.country || data.countryName || "United States";
        const countryCode = data.country_code || data.countryCode || (data.country && data.country.iso_code) || null;
        const detectedFlag = countryCode ? `https://flagcdn.com/${countryCode.toLowerCase()}.svg` : "https://flagcdn.com/us.svg";

        // Only set if user hasn't manually set a preference
        if (!localStorage.getItem("currency") && !hasDetectedLocation.current) {
            setCurrency(detectedCurrency);
            localStorage.setItem("currency", detectedCurrency);
            hasDetectedLocation.current = true;
          }

        if (!localStorage.getItem("country")) {
          setCountry(detectedCountry);
          localStorage.setItem("country", detectedCountry);
        }
        if (!localStorage.getItem("flag")) {
          setFlag(detectedFlag);
          localStorage.setItem("flag", detectedFlag);
        }
      } catch (err) {
        console.error("❌ Failed to detect location:", err);
        // fallback values already set in state initialization; no override
      } finally {
        setLoading(false);
      }
    };

    // run in parallel
    Promise.all([fetchRates(), detectUserLocation()]).finally(() => {
      // ensure loading flips off if detectUserLocation didn't (defensive)
      setLoading(false);
    });
  }, []);

  // Manual currency switcher
  const switchCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem("currency", newCurrency);
  };

  const convertDisplay = useCallback(
    (amountUSD) => {
      if (!exchangeRates || loading) return "...";
      const rate = exchangeRates[currency] ?? 1;
      const local = amountUSD * rate;
      return local.toFixed(2);
    },
    [exchangeRates, currency, loading]
  );

  const toUSD = useCallback(
    (amountLocal) => {
      if (!exchangeRates || loading) return null;
      const rate = exchangeRates[currency] ?? 1;
      return Number((amountLocal / rate).toFixed(2));
    },
    [exchangeRates, currency, loading]
  );

  return {
    convertDisplay,
    toUSD,
    currency,
    country,
    flag,
    loading,
    switchCurrency,
  };
};

export default useCurrencyConverter;
